import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/constants';
import Modal from './Modal';
import ColorSelector from './ColorSelector';
import TechnicalSpecSheet, { TechnicalSpec } from './TechnicalSpecSheet';
import FabricSelector from './FabricSelector'; // Yeni import
import AccessorySelector from './AccessorySelector';
import { UploadOutlined, DeleteOutlined, FilePdfOutlined, FileExcelOutlined, FileOutlined, FileZipOutlined, FileImageOutlined } from '@ant-design/icons'; // Ant Design ikonları eklendi
import { Select, Input, Button as AntButton, notification, Spin, Tooltip, Divider, Space, Alert, Tabs } from 'antd'; // Ant Design bileşenleri eklendi
import { Card, SectionTitle } from './SharedComponents'; // Paylaşılan bileşenleri içe aktar

interface Color {
  _id: string;
  name: string;
  firmCode?: string; // firmCode opsiyonel yapıldı
  tcxCode?: string; // tcxCode opsiyonel yapıldı
  hexCode?: string; // hexCode eklendi
}

interface Accessory {
  _id: string;
  name: string;
  quantity: number;
  description?: string;
}

// Teknik föy için ek interface'ler
interface SizeSpec {
  size: string;
  chest: number;
  waist: number;
  hip: number;
  length: number;
  sleeve: number;
  [key: string]: string | number; // Dinamik ölçü alanları için
}

interface StitchDetail {
  location: string;
  stitchType: string;
  threadType: string;
  stitchesPerInch: number;
  notes: string;
}
interface ProductFormData {
  modelName: string;
  purchasingGroup: 'knitting' | 'weaving' | 'triko' | 'denim'; // Güncellendi
  modelStyle: string; // Yeni alan
  modelImage: File | null;
  fabric: string;
  color: string;
  accessories: Accessory[];
  description: string;
  status: string;
  isPrototype: boolean;
}

interface Fabric {
  _id: string;
  name: string;
  code: string;
  description?: string;
}

interface FabricSpec {
  composition: string;
  weight: string;
  construction: string;
  supplier: string;
  colorFastness: string;
  shrinkage: string;
}

interface PrintingDetail {
  technique: string;
  location: string;
  colors: string;
  size: string;
  notes: string;
}

interface TechnicalSpec {
  sizeSpecs: SizeSpec[];
  stitchDetails: StitchDetail[];
  fabricSpecs: FabricSpec[];
  printingDetails: PrintingDetail[];
  technicalDrawingUrls?: string[]; // Teknik çizim URL'leri eklendi
  measurementGuideUrls?: string[]; // Ölçüm şekli görsel URL'leri
  careInstructions: string[];
  qualityStandards: string[];
  notes: string;
}

// Prototip kodu oluşturma fonksiyonu
const generatePrototypeCode = () => {
  // BCP + 6 rakamlı rastgele sayı
  const randomDigits = Math.floor(100000 + Math.random() * 900000); // 100000-999999 arası
  return `BCP${randomDigits}`;
};

const ProductCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState<Color | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [technicalDrawingFiles, setTechnicalDrawingFiles] = useState<File[]>([]);
  const [technicalDrawingPreviewUrls, setTechnicalDrawingPreviewUrls] = useState<string[]>([]);
  const [measurementGuideFiles, setMeasurementGuideFiles] = useState<File[]>([]);
  const [measurementGuidePreviewUrls, setMeasurementGuidePreviewUrls] = useState<string[]>([]);
  const [accessories, setAccessories] = useState<Accessory[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedFabric, setSelectedFabric] = useState<Fabric | null>(null);
  const [isDragging, setIsDragging] = useState(false); // Ana görseller için
  const [isTechDrawingDragging, setIsTechDrawingDragging] = useState(false); // Teknik çizimler için
  const [isMeasurementGuideDragging, setIsMeasurementGuideDragging] = useState(false); // Ölçüm şekilleri için

  // Model dosyaları için state'ler
  const [modelDocumentFiles, setModelDocumentFiles] = useState<File[]>([]);
  const [modelDocumentPreviewUrls, setModelDocumentPreviewUrls] = useState<string[]>([]);
  const [isModelDocDragging, setIsModelDocDragging] = useState(false);

  const handleFabricSelect = (fabric: Fabric) => {
    setSelectedFabric(fabric);
    setFormData(prev => ({ ...prev, fabric: fabric._id }));
  };

  // Define ProductFormData locally or import if shared
  interface ProductFormDataState {
    modelName: string;
    purchasingGroup: 'knitting' | 'weaving' | 'triko' | 'denim';
    modelStyle: string;
    // modelImage is handled by selectedFiles state
    fabric: string; // Store fabric ID
    color: string; // Store color ID
    // accessories are handled by accessories state
    description: string;
    status: string;
    isPrototype: boolean;
    prototypeCode?: string; // Optional prototype code
  }

  const [formData, setFormData] = useState<ProductFormDataState>({
    modelName: '',
    purchasingGroup: 'knitting',
    modelStyle: '',
    fabric: '',
    color: '',
    description: '',
    status: 'draft',
    isPrototype: true,
    prototypeCode: generatePrototypeCode(),
  });

  // Teknik föy state'leri
  const [showTechSpec, setShowTechSpec] = useState<boolean>(true); // true olarak değiştirin
  const [techSpec, setTechSpec] = useState<TechnicalSpec>({
    sizeSpecs: [], // Boş array ile başlat
    stitchDetails: [],
    fabricSpecs: [{
      composition: '',
      weight: '',
      construction: '',
      supplier: '',
      colorFastness: '',
      shrinkage: ''
    }],
    printingDetails: [],
    technicalDrawingUrls: [], // Initialize edildi
    measurementGuideUrls: [], // Initialize edildi
    careInstructions: ['30°C normal yıkama', 'Ağartıcı kullanılmaz', 'Orta ısıda ütülenir', 'Kuru temizleme yapılmaz'],
    qualityStandards: [],
    notes: ''
  });
  
  // Dinamik ölçü alanları
  const [customSizeFields, setCustomSizeFields] = useState<string[]>([]);
  
  // Yeni beden ekleme
  const addSizeSpec = () => {
    setTechSpec({
      ...techSpec,
      sizeSpecs: [
        ...techSpec.sizeSpecs,
        { size: '', chest: 0, waist: 0, hip: 0, length: 0, sleeve: 0 }
      ]
    });
  };
  
  // Beden silme
  const removeSizeSpec = (index: number) => {
    const updatedSizeSpecs = [...techSpec.sizeSpecs];
    updatedSizeSpecs.splice(index, 1);
    setTechSpec({
      ...techSpec,
      sizeSpecs: updatedSizeSpecs
    });
  };
  
  // Beden bilgilerini güncelleme
  const updateSizeSpec = (index: number, field: string, value: string | number) => {
    const updatedSizeSpecs = [...techSpec.sizeSpecs];
    updatedSizeSpecs[index] = {
      ...updatedSizeSpecs[index],
      [field]: field === 'size' ? value : Number(value)
    };
    setTechSpec({
      ...techSpec,
      sizeSpecs: updatedSizeSpecs
    });
  };
  
  // Özel ölçü alanı ekleme
  const addCustomSizeField = (fieldName: string) => {
    if (fieldName && !customSizeFields.includes(fieldName)) {
      setCustomSizeFields([...customSizeFields, fieldName]);
      
      // Tüm beden kayıtlarına yeni alanı ekle
      const updatedSizeSpecs = techSpec.sizeSpecs.map(spec => ({
        ...spec,
        [fieldName]: 0
      }));
      
      setTechSpec({
        ...techSpec,
        sizeSpecs: updatedSizeSpecs
      });
    }
  };
  
  // Dikiş detayı ekleme
  const addStitchDetail = () => {
    setTechSpec({
      ...techSpec,
      stitchDetails: [
        ...techSpec.stitchDetails,
        { location: '', stitchType: '', threadType: '', stitchesPerInch: 0, notes: '' }
      ]
    });
  };
  
  // Dikiş detayını güncelleme
  const updateStitchDetail = (index: number, field: string, value: string | number) => {
    const updatedStitchDetails = [...techSpec.stitchDetails];
    updatedStitchDetails[index] = {
      ...updatedStitchDetails[index],
      [field]: field === 'stitchesPerInch' ? Number(value) : value
    };
    setTechSpec({
      ...techSpec,
      stitchDetails: updatedStitchDetails
    });
  };
  
  // Dikiş detayı silme
  const removeStitchDetail = (index: number) => {
    const updatedStitchDetails = [...techSpec.stitchDetails];
    updatedStitchDetails.splice(index, 1);
    setTechSpec({
      ...techSpec,
      stitchDetails: updatedStitchDetails
    });
  };
  
  // Kumaş bilgilerini güncelleme
  const updateFabricSpec = (index: number, field: string, value: string) => {
    const updatedFabricSpecs = [...techSpec.fabricSpecs];
    updatedFabricSpecs[index] = {
      ...updatedFabricSpecs[index],
      [field]: value
    };
    setTechSpec({
      ...techSpec,
      fabricSpecs: updatedFabricSpecs
    });
  };
  
  // Baskı detayı ekleme
  const addPrintingDetail = () => {
    setTechSpec({
      ...techSpec,
      printingDetails: [
        ...techSpec.printingDetails,
        { technique: '', location: '', colors: '', size: '', notes: '' }
      ]
    });
  };
  
  // Baskı detayını güncelleme
  const updatePrintingDetail = (index: number, field: string, value: string) => {
    const updatedPrintingDetails = [...techSpec.printingDetails];
    updatedPrintingDetails[index] = {
      ...updatedPrintingDetails[index],
      [field]: value
    };
    setTechSpec({
      ...techSpec,
      printingDetails: updatedPrintingDetails
    });
  };
  
  // Baskı detayı silme
  const removePrintingDetail = (index: number) => {
    const updatedPrintingDetails = [...techSpec.printingDetails];
    updatedPrintingDetails.splice(index, 1);
    setTechSpec({
      ...techSpec,
      printingDetails: updatedPrintingDetails
    });
  };
  
  // Bakım talimatı ekleme
  const addCareInstruction = (instruction: string) => {
    if (instruction) {
      setTechSpec({
        ...techSpec,
        careInstructions: [...techSpec.careInstructions, instruction]
      });
    }
  };
  
  // Bakım talimatı silme
  const removeCareInstruction = (index: number) => {
    const updatedCareInstructions = [...techSpec.careInstructions];
    updatedCareInstructions.splice(index, 1);
    setTechSpec({
      ...techSpec,
      careInstructions: updatedCareInstructions
    });
  };
  
  // Kalite standardı ekleme
  const addQualityStandard = (standard: string) => {
    if (standard) {
      setTechSpec({
        ...techSpec,
        qualityStandards: [...techSpec.qualityStandards, standard]
      });
    }
  };
  
  // Notları güncelleme
  const updateNotes = (notes: string) => {
    setTechSpec({
      ...techSpec,
      notes
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'isPrototype') {
      setFormData(prev => ({
        ...prev,
        [name]: value === 'true'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };

  // Ant Design Select için özel handleChange fonksiyonu
  const handleSelectChange = (name: keyof ProductFormDataState) => (value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileDropOrSelect = useCallback((acceptedFiles: File[]) => {
    console.log('[CreateProduct] 🖼️ Dosyalar alındı:', acceptedFiles.map(f => f.name));
    const currentFileCount = selectedFiles.length;
    const maxFiles = 20;
    const availableSlots = maxFiles - currentFileCount;

    if (acceptedFiles.length === 0) return;

    if (acceptedFiles.length > availableSlots) {
      alert(`En fazla ${maxFiles} görsel yükleyebilirsiniz. Sadece ${availableSlots} dosya eklenecek.`);
      acceptedFiles = acceptedFiles.slice(0, availableSlots);
    }

    // Dosya boyutu ve tipi kontrolü (isteğe bağlı)
    const validFiles = acceptedFiles.filter(file => {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert(`${file.name} dosyası çok büyük (Maks 5MB).`);
        return false;
      }
      if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        alert(`${file.name} dosya tipi desteklenmiyor (JPEG, PNG, GIF, WebP).`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      const newFiles = [...selectedFiles, ...validFiles];
      setSelectedFiles(newFiles);

      const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
      setImagePreviewUrls(prevUrls => [...prevUrls, ...newPreviewUrls]);
      console.log(`[CreateProduct] ✅ ${validFiles.length} geçerli dosya eklendi. Toplam: ${newFiles.length}`);
    }
  }, [selectedFiles]); // selectedFiles bağımlılığı eklendi

  // Input ile dosya seçimi
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileDropOrSelect(Array.from(e.target.files));
    }
    // Input'u sıfırla ki aynı dosya tekrar seçilebilsin
    e.target.value = '';
  };

  // Sürükleme olayları
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    console.log('[CreateProduct] Drag Enter');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Sadece dışarı çıkıldığında false yap
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
    }
    setIsDragging(false);
    console.log('[CreateProduct] Drag Leave');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Sürükleme sırasında cursor'ı ayarla (isteğe bağlı)
    e.dataTransfer.dropEffect = 'copy';
    if (!isDragging) setIsDragging(true); // Bazen leave tetiklenmeyebilir
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    console.log('[CreateProduct] Drop');
    const files = Array.from(e.dataTransfer.files);
    handleFileDropOrSelect(files);
  };

  const removeImage = (index: number) => {
    const updatedFiles = [...selectedFiles];
    const updatedUrls = [...imagePreviewUrls];
    
    // URL'yi serbest bırak
    URL.revokeObjectURL(updatedUrls[index]);
    
    // Dizilerden kaldır
    updatedFiles.splice(index, 1);
    updatedUrls.splice(index, 1);
    
    setSelectedFiles(updatedFiles);
    setImagePreviewUrls(updatedUrls);
  };

  // --- Genel Dosya Yükleme Fonksiyonu ---
  const handleGenericFileDropOrSelect = useCallback((
    acceptedFiles: File[],
    currentFiles: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    setPreviewUrls: React.Dispatch<React.SetStateAction<string[]>>,
    maxFiles: number,
    fileTypeLabel: string // e.g., "teknik çizim", "ölçüm şekli"
  ) => {
    console.log(`[CreateProduct] 📥 ${fileTypeLabel} dosyaları alındı:`, acceptedFiles.map(f => f.name));
    const currentFileCount = currentFiles.length;
    const availableSlots = maxFiles - currentFileCount;

    if (acceptedFiles.length === 0) return;

    if (acceptedFiles.length > availableSlots) {
      alert(`En fazla ${maxFiles} ${fileTypeLabel} yükleyebilirsiniz. Sadece ${availableSlots} dosya eklenecek.`);
      acceptedFiles = acceptedFiles.slice(0, availableSlots);
    }

    // Dosya boyutu ve tipi kontrolü
    const validFiles = acceptedFiles.filter(file => {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert(`${file.name} dosyası çok büyük (Maks 5MB).`);
        return false;
      }
      if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'].includes(file.type)) { // PDF eklendi (isteğe bağlı)
        alert(`${file.name} dosya tipi desteklenmiyor (JPEG, PNG, GIF, WebP, PDF).`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      const newFiles = [...currentFiles, ...validFiles];
      setFiles(newFiles);

      const newPreviewUrls = validFiles.map(file =>
        file.type === 'application/pdf' ? '/pdf-icon.png' : URL.createObjectURL(file) // PDF için ikon yolu
      );
      setPreviewUrls(prevUrls => [...prevUrls, ...newPreviewUrls]);
      console.log(`[CreateProduct] ✅ ${validFiles.length} geçerli ${fileTypeLabel} dosyası eklendi. Toplam: ${newFiles.length}`);
    }
  }, []); // Bağımlılık yok, çünkü state'ler argüman olarak geliyor

  // --- Teknik Çizim Handler'ları ---
  const handleTechDrawFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleGenericFileDropOrSelect(
        Array.from(e.target.files),
        technicalDrawingFiles,
        setTechnicalDrawingFiles,
        setTechnicalDrawingPreviewUrls,
        5,
        "teknik çizim"
      );
    }
    e.target.value = '';
  };
  const handleTechDrawDragEnter = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setIsTechDrawingDragging(true); };
  const handleTechDrawDragLeave = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsTechDrawingDragging(false); };
  const handleTechDrawDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; if (!isTechDrawingDragging) setIsTechDrawingDragging(true); };
  const handleTechDrawDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsTechDrawingDragging(false);
    handleGenericFileDropOrSelect(
      Array.from(e.dataTransfer.files),
      technicalDrawingFiles,
      setTechnicalDrawingFiles,
      setTechnicalDrawingPreviewUrls,
      5,
      "teknik çizim"
    );
  };
  const removeTechnicalDrawingImage = (index: number) => {
    const url = technicalDrawingPreviewUrls[index];
    if (url !== '/pdf-icon.png') { // Sadece obje URL'lerini revoke et
        URL.revokeObjectURL(url);
    }
    setTechnicalDrawingFiles(prev => prev.filter((_, i) => i !== index));
    setTechnicalDrawingPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // --- Ölçüm Şekli Handler'ları ---
  const handleMeasureGuideFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleGenericFileDropOrSelect(
        Array.from(e.target.files),
        measurementGuideFiles,
        setMeasurementGuideFiles,
        setMeasurementGuidePreviewUrls,
        5,
        "ölçüm şekli"
      );
    }
    e.target.value = '';
  };
  const handleMeasureGuideDragEnter = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setIsMeasurementGuideDragging(true); };
  const handleMeasureGuideDragLeave = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsMeasurementGuideDragging(false); };
  const handleMeasureGuideDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; if (!isMeasurementGuideDragging) setIsMeasurementGuideDragging(true); };
  const handleMeasureGuideDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsMeasurementGuideDragging(false);
    handleGenericFileDropOrSelect(
      Array.from(e.dataTransfer.files),
      measurementGuideFiles,
      setMeasurementGuideFiles,
      setMeasurementGuidePreviewUrls,
      5,
      "ölçüm şekli"
    );
  };
  const removeMeasurementGuideImage = (index: number) => {
    const url = measurementGuidePreviewUrls[index];
     if (url !== '/pdf-icon.png') {
         URL.revokeObjectURL(url);
     }
    setMeasurementGuideFiles(prev => prev.filter((_, i) => i !== index));
    setMeasurementGuidePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Preview URL'lerini temizleme useEffect'i
  useEffect(() => {
    return () => {
      console.log('[CreateProduct] Component unmount ediliyor, önizleme URL\'leri temizleniyor...');
      imagePreviewUrls.forEach(url => {
          console.log(`  Revoking image URL: ${url}`);
          URL.revokeObjectURL(url);
      });
      technicalDrawingPreviewUrls.forEach(url => {
          console.log(`  Revoking technical drawing URL: ${url}`);
          URL.revokeObjectURL(url);
      });
      measurementGuidePreviewUrls.forEach(url => {
          console.log(`  Revoking measurement guide URL: ${url}`);
          URL.revokeObjectURL(url);
      });
      modelDocumentPreviewUrls.forEach(url => {
          if (url !== '/pdf-icon.png' && url !== '/excel-icon.png' && url !== '/word-icon.png' && 
              url !== '/zip-icon.png' && url !== '/file-icon.png') URL.revokeObjectURL(url);
      });
    };
  }, [imagePreviewUrls, technicalDrawingPreviewUrls, measurementGuidePreviewUrls, modelDocumentPreviewUrls]);

  const handleColorSelect = (color: Color) => {
    setSelectedColor(color);
    setFormData(prev => ({ ...prev, color: color._id }));
  };

  // Update handleAccessoryUpdate to match AccessorySelector's expected prop
  const handleAccessoryUpdate = (updatedAccessories: Accessory[]) => {
      console.log('[CreateProduct] Aksesuarlar güncellendi:', updatedAccessories);
      setAccessories(updatedAccessories);
  };

  // Yeni handleAccessorySelect fonksiyonu
  const handleAccessorySelect = (accessory: Accessory) => {
    setAccessories(prevAccessories => {
      const existingIndex = prevAccessories.findIndex(acc => acc._id === accessory._id);
      if (existingIndex > -1) {
        // Mevcut aksesuarı güncelle
        const updatedList = [...prevAccessories];
        updatedList[existingIndex] = { ...accessory, quantity: Number(accessory.quantity) || 0 };
        console.log('[CreateProduct] Aksesuar güncellendi:', updatedList);
        return updatedList;
      } else {
        // Yeni aksesuar ekle
        const updatedList = [...prevAccessories, { ...accessory, quantity: Number(accessory.quantity) || 0 }];
        console.log('[CreateProduct] Yeni aksesuar eklendi:', updatedList);
        return updatedList;
      }
    });
  };

  // Yeni handleAccessoryRemove fonksiyonu
  const handleAccessoryRemove = (accessoryIdToRemove: string) => {
    console.log(`[CreateProduct] Aksesuar kaldırılıyor (ID: ${accessoryIdToRemove})`);
    setAccessories(prevAccessories => {
      const updatedList = prevAccessories.filter(acc => acc._id !== accessoryIdToRemove);
      console.log('[CreateProduct] Aksesuar kaldırıldıktan sonra liste:', updatedList);
      return updatedList;
    });
  };

  // isPrototype değiştiğinde prototip kodunu güncelleyelim
  useEffect(() => {
    if (formData.isPrototype) {
      setFormData(prev => ({
        ...prev, 
        prototypeCode: generatePrototypeCode()
      }));
    }
  }, [formData.isPrototype]);

  // Model dosyası yükleme işlemleri
  const handleModelDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleModelDocFilesSelected(Array.from(e.target.files));
    }
  };

  const handleModelDocDragEnter = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsModelDocDragging(true); 
  };
  
  const handleModelDocDragLeave = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsModelDocDragging(false); 
  };
  
  const handleModelDocDragOver = (e: React.DragEvent<HTMLDivElement>) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    e.dataTransfer.dropEffect = 'copy'; 
    if (!isModelDocDragging) setIsModelDocDragging(true); 
  };
  
  const handleModelDocDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModelDocDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleModelDocFilesSelected(Array.from(e.dataTransfer.files));
    }
  };

  const handleModelDocFilesSelected = (files: File[]) => {
    // Maksimum dosya sayısını kontrol et
    const remainingSlots = 20 - modelDocumentFiles.length;
    if (remainingSlots <= 0) {
      alert('En fazla 20 model dosyası yükleyebilirsiniz.');
      return;
    }

    const newFiles = files.slice(0, remainingSlots);
    setModelDocumentFiles(prevFiles => [...prevFiles, ...newFiles]);
    
    // Dosya tiplerine göre önizleme oluştur
    const newPreviewUrls = newFiles.map(file => {
      if (file.type.includes('pdf')) {
        return '/pdf-icon.png'; // PDF için yer tutucu
      } else if (file.type.includes('spreadsheet') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        return '/excel-icon.png'; // Excel için yer tutucu
      } else if (file.type.includes('document') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) {
        return '/word-icon.png'; // Word için yer tutucu
      } else if (file.type.includes('zip') || file.name.endsWith('.zip') || file.name.endsWith('.rar')) {
        return '/zip-icon.png'; // Arşiv için yer tutucu
      } else {
        return '/file-icon.png'; // Varsayılan dosya ikonu
      }
    });
    
    setModelDocumentPreviewUrls(prevUrls => [...prevUrls, ...newPreviewUrls]);
  };

  const removeModelDocumentFile = (index: number) => {
    setModelDocumentFiles(prevFiles => {
      const updatedFiles = [...prevFiles];
      updatedFiles.splice(index, 1);
      return updatedFiles;
    });
    
    setModelDocumentPreviewUrls(prevUrls => {
      const updatedUrls = [...prevUrls];
      updatedUrls.splice(index, 1);
      return updatedUrls;
    });
  };

  // Ürün verilerini gönder - teknik föye odaklanmış yeni implementasyon
const handleSubmit = async () => {
  console.log('[CreateProduct] 📝 Form gönderiliyor...');
  console.log('[CreateProduct] 💾 Mevcut aksesuarlar:', accessories);
  console.log(`[CreateProduct] 🖼️ Seçili ana görsel dosyaları: ${selectedFiles.length} adet`);
  selectedFiles.forEach((file, index) => console.log(`  [${index}]: ${file.name}`));
  console.log(`[CreateProduct] 📏 Seçili teknik çizim dosyaları: ${technicalDrawingFiles.length} adet`);
  technicalDrawingFiles.forEach((file, index) => console.log(`  [${index}]: ${file.name}`));
  console.log(`[CreateProduct] 📐 Seçili ölçüm şekli dosyaları: ${measurementGuideFiles.length} adet`);
  measurementGuideFiles.forEach((file, index) => console.log(`  [${index}]: ${file.name}`));

  try {
    // Form validasyonu
    if (!formData.modelName) {
      alert("Model adı zorunludur.");
      return;
    }
    if (!formData.fabric) {
      alert("Kumaş bilgisi zorunludur.");
      return;
    }
    if (!formData.color) {
      alert("Renk seçimi zorunludur.");
      return;
    }
    
    setLoading(true);
    
    // 1. Görselleri yükle
    let imageUrls: string[] = [];
    if (selectedFiles.length > 0) {
      console.log('[CreateProduct] 🖼️ Ana görseller yükleniyor...');
      try {
        const imageFormData = new FormData();
        selectedFiles.forEach(file => {
          imageFormData.append('images', file); // Backend'deki 'images' alan adıyla eşleşmeli
        });
        
        const imageResponse = await fetch(`${API_URL}/products/upload-multiple-images`, {
          method: 'POST',
          body: imageFormData,
        });

        if (!imageResponse.ok) {
          const errorText = await imageResponse.text();
          console.error(`[CreateProduct] ❌ Görsel yükleme hatası: ${errorText}`);
          throw new Error(`Görseller yüklenirken bir hata oluştu: ${errorText}`);
        }

        const imageData = await imageResponse.json();
        imageUrls = imageData.imageUrls || [];
        console.log(`[CreateProduct] ✅ Ana görseller yüklendi (${imageUrls.length} adet):`, imageUrls);
      } catch (uploadError) {
        console.error('[CreateProduct] ❌ Görsel yükleme sırasında hata:', uploadError);
        alert(`Görsel yükleme hatası: ${uploadError instanceof Error ? uploadError.message : 'Bilinmeyen hata'}`);
        setLoading(false); // Hata durumunda yükleniyor state'ini kapat
        return; // İşlemi durdur
      }
    } else {
      console.log('[CreateProduct] 🖼️ Ana görsel seçilmedi.');
    }

    // 2. Teknik Çizimleri Yükle
    let technicalDrawingUrlsResult: string[] = [];
    if (technicalDrawingFiles.length > 0) {
      console.log('[CreateProduct] 📏 Teknik çizimler yükleniyor...');
      try {
        const drawingFormData = new FormData();
        technicalDrawingFiles.forEach(file => {
          drawingFormData.append('images', file); // Backend'deki 'images' alan adıyla eşleşmeli
        });

        // Teknik çizim olduğunu belirtmek için query parametresi ekleyebiliriz (opsiyonel, backend'e bağlı)
        const uploadUrl = `${API_URL}/products/upload-multiple-images?type=technical`; 
        // VEYA özel endpoint kullanılıyorsa: `${API_URL}/products/upload-technical-drawings`;
        
        console.log(`[CreateProduct] 🚀 Teknik çizim yükleme isteği: POST ${uploadUrl}`);

        const drawingResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: drawingFormData,
        });

        if (!drawingResponse.ok) {
          const errorText = await drawingResponse.text();
          console.error(`[CreateProduct] ❌ Teknik çizim yükleme hatası: ${errorText}`);
          throw new Error(`Teknik çizimler yüklenirken bir hata oluştu: ${errorText}`);
        }

        const drawingData = await drawingResponse.json();
        technicalDrawingUrlsResult = drawingData.imageUrls || [];
        console.log(`[CreateProduct] ✅ Teknik çizimler yüklendi (${technicalDrawingUrlsResult.length} adet):`, technicalDrawingUrlsResult);
      } catch (uploadError) {
        console.error('[CreateProduct] ❌ Teknik çizim yükleme sırasında hata:', uploadError);
        alert(`Teknik çizim yükleme hatası: ${uploadError instanceof Error ? uploadError.message : 'Bilinmeyen hata'}`);
        setLoading(false); // Hata durumunda yükleniyor state'ini kapat
        return; // İşlemi durdur
      }
    } else {
      console.log('[CreateProduct] 📏 Teknik çizim dosyası seçilmedi.');
    }

    // 3. Ölçüm Şekli Görsellerini Yükle
    let measurementGuideUrlsResult: string[] = [];
    if (measurementGuideFiles.length > 0) {
      console.log('[CreateProduct] 📐 Ölçüm şekli görselleri yükleniyor...');
      try {
        const guideFormData = new FormData();
        measurementGuideFiles.forEach(file => {
          guideFormData.append('images', file); // Aynı endpoint'i kullanıyoruz
        });

        // İsteğe bağlı: type=measurement gibi bir query parametresi eklenebilir
        const uploadUrl = `${API_URL}/products/upload-multiple-images`;
        console.log(`[CreateProduct] 🚀 Ölçüm şekli yükleme isteği: POST ${uploadUrl}`);

        const guideResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: guideFormData,
        });

        if (!guideResponse.ok) {
          const errorText = await guideResponse.text();
          console.error(`[CreateProduct] ❌ Ölçüm şekli yükleme hatası: ${errorText}`);
          throw new Error(`Ölçüm şekli görselleri yüklenirken bir hata oluştu: ${errorText}`);
        }

        const guideData = await guideResponse.json();
        measurementGuideUrlsResult = guideData.imageUrls || [];
        console.log(`[CreateProduct] ✅ Ölçüm şekli görselleri yüklendi (${measurementGuideUrlsResult.length} adet):`, measurementGuideUrlsResult);
      } catch (uploadError) {
        console.error('[CreateProduct] ❌ Ölçüm şekli yükleme sırasında hata:', uploadError);
        alert(`Ölçüm şekli yükleme hatası: ${uploadError instanceof Error ? uploadError.message : 'Bilinmeyen hata'}`);
        setLoading(false);
        return;
      }
    } else {
      console.log('[CreateProduct] 📐 Ölçüm şekli dosyası seçilmedi.');
    }

    // 4. Model Dosyalarını Yükle
    let modelDocumentUrlsResult: string[] = [];
    if (modelDocumentFiles.length > 0) {
      console.log('[CreateProduct] 📂 Model dosyaları yükleniyor...');
      try {
        const modelDocFormData = new FormData();
        modelDocumentFiles.forEach(file => {
          modelDocFormData.append('documents', file);
        });

        const uploadUrl = `${API_URL}/model-documents/upload`;
        console.log(`[CreateProduct] 🚀 Model dosyası yükleme isteği: POST ${uploadUrl}`);

        const modelDocResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: modelDocFormData,
        });

        if (!modelDocResponse.ok) {
          const errorText = await modelDocResponse.text();
          console.error(`[CreateProduct] ❌ Model dosyası yükleme hatası: ${errorText}`);
          throw new Error(`Model dosyaları yüklenirken bir hata oluştu: ${errorText}`);
        }

        const modelDocData = await modelDocResponse.json();
        modelDocumentUrlsResult = modelDocData.documents.map((doc: any) => doc.path) || [];
        console.log(`[CreateProduct] ✅ Model dosyaları yüklendi (${modelDocumentUrlsResult.length} adet):`, modelDocumentUrlsResult);
      } catch (uploadError) {
        console.error('[CreateProduct] ❌ Model dosyası yükleme sırasında hata:', uploadError);
        alert(`Model dosyası yükleme hatası: ${uploadError instanceof Error ? uploadError.message : 'Bilinmeyen hata'}`);
        setLoading(false);
        return;
      }
    } else {
      console.log('[CreateProduct] 📂 Model dosyası seçilmedi.');
    }

    // ÖNEMLİ: Teknik föyü doğrula ve temizle
    console.log('[CreateProduct] 📐 Teknik föy verisi normalize ediliyor...');
    const normalizedTechSpec: TechnicalSpec = {
      // ... (normalization logic - no changes needed here based on the prompt) ...
      sizeSpecs: techSpec.sizeSpecs
        .filter(spec => spec.size.trim() !== '')
        .map(spec => ({
          size: spec.size,
          chest: Number(spec.chest) || 0,
          waist: Number(spec.waist) || 0,
          hip: Number(spec.hip) || 0,
          length: Number(spec.length) || 0,
          sleeve: Number(spec.sleeve) || 0,
          ...customSizeFields.reduce((acc, field) => ({
            ...acc,
            [field]: Number(spec[field]) || 0
          }), {})
        })),
      fabricSpecs: techSpec.fabricSpecs
        .filter(spec => spec.composition?.trim() !== ''),
      stitchDetails: techSpec.stitchDetails
        .filter(detail => 
          detail.location.trim() !== '' || 
          detail.stitchType.trim() !== '' || 
          detail.threadType.trim() !== ''),
      printingDetails: techSpec.printingDetails
        .filter(detail => 
          detail.technique?.trim() !== '' || 
          detail.location?.trim() !== ''),
      careInstructions: techSpec.careInstructions || [],
      qualityStandards: techSpec.qualityStandards || [],
      notes: techSpec.notes || '',
      technicalDrawingUrls: technicalDrawingUrlsResult, // Yüklenen URL'leri ekle
      measurementGuideUrls: measurementGuideUrlsResult, // Yüklenen URL'leri ekle
      modelDocumentUrls: modelDocumentUrlsResult
    };

    console.log('[CreateProduct] ✅ Normalize edilmiş teknik föy:', JSON.stringify(normalizedTechSpec, null, 2));

    // Ürün verilerini hazırla
    const productData = {
      ...formData,
      purchasingGroup: formData.purchasingGroup.toLowerCase(), // Güncellendi
      modelStyle: formData.modelStyle, // Yeni alan eklendi
      imageUrls: imageUrls, // Yüklenen ana görsel URL'leri
      accessories: accessories.map(acc => ({ // Aksesuarları doğru formatta map'le
        _id: acc._id,
        name: acc.name,
        // type: acc.type || '', // Eğer type alanı varsa
        quantity: String(acc.quantity), // Quantity'yi string yap
        description: acc.description || ''
      })),
      technicalSpec: normalizedTechSpec // Normalize edilmiş teknik föy
    };

    // API çağrısı
    console.log('[CreateProduct] 📦 API\'ye gönderilecek son ürün verisi:', JSON.stringify(productData, null, 2));
    
    const response = await fetch(`${API_URL}/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || `HTTP hata kodu: ${response.status}`;
        console.error('[CreateProduct] ❌ API hata detayları:', errorData);
      } catch (e) {
        errorMessage = await response.text() || `HTTP hata kodu: ${response.status}`;
      }
      console.error(`[CreateProduct] ❌ Ürün oluşturma API hatası: ${errorMessage}`);
      throw new Error(`Ürün oluşturulurken bir hata oluştu: ${errorMessage}`);
    }

    const result = await response.json();
    console.log('[CreateProduct] ✅ Ürün başarıyla oluşturuldu:', result);
    
    alert('Ürün başarıyla oluşturuldu!');
    navigate('/products');
  } catch (error) {
    console.error('[CreateProduct] ❌ İşlem sırasında genel hata:', error);
    alert('Ürün oluşturulurken bir hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
  } finally {
    setLoading(false);
    setConfirmModalOpen(false); // Modalı kapat
  }
};

  return (
    <div className="p-4 md:p-8 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 sm:mb-0">Yeni Ürün Oluştur</h1>
          <Space wrap>
            <AntButton onClick={() => setCancelModalOpen(true)} disabled={loading}>İptal</AntButton>
            <AntButton type="primary" onClick={() => setConfirmModalOpen(true)} loading={loading}>
              {loading ? 'Kaydediliyor...' : 'Ürün Oluştur'}
            </AntButton>
          </Space>
        </div>

        {/* Main Content with Tabs */}
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Temel Bilgiler" key="1">
            <div className="space-y-8 mt-4">
              {/* Temel Bilgiler Card */}
          <Card>
                <SectionTitle>Ürün Bilgileri</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Satın Alma Grubu <span className="text-red-500">*</span>
                </label>
                {/* HTML select yerine Ant Design Select kullan */}
                <Select
                  value={formData.purchasingGroup}
                  onChange={handleSelectChange('purchasingGroup')}
                  className="w-full" // Ant Design Select'in genişliğini ayarla
                  // style={{ width: '100%' }} // Alternatif olarak stil ile genişlik ayarı
                >
                  <Select.Option value="knitting">Örme</Select.Option>
                  <Select.Option value="weaving">Dokuma</Select.Option>
                  <Select.Option value="triko">Triko</Select.Option>
                  <Select.Option value="denim">Denim</Select.Option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Stili
                </label>
                {/* HTML select yerine Ant Design Select kullan */}
                <Select
                  value={formData.modelStyle}
                  onChange={handleSelectChange('modelStyle')}
                  className="w-full"
                  allowClear // Temizleme butonu ekle (isteğe bağlı)
                >
                  <Select.Option value="">Seçiniz...</Select.Option>
                  <Select.Option value="Baskı">Baskı</Select.Option>
                  <Select.Option value="Düz">Düz</Select.Option>
                  <Select.Option value="Nakışlı">Nakışlı</Select.Option>
                  <Select.Option value="Taşlı">Taşlı</Select.Option>
                  {/* İhtiyaç duyulan diğer stilleri buraya ekleyebilirsiniz */}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kumaş <span className="text-red-500">*</span>
                </label>
                <FabricSelector
                  selectedFabric={selectedFabric}
                  onFabricSelect={handleFabricSelect}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Renk <span className="text-red-500">*</span>
                </label>
                <ColorSelector
                  selectedColor={selectedColor}
                  onColorSelect={handleColorSelect}
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ürün Görselleri (Maksimum 20 adet)
                </label>
                {/* Sürükle Bırak Alanı */}
                <div
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ease-in-out
                    ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'}
                    ${selectedFiles.length >= 20 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  onClick={() => selectedFiles.length < 20 && document.getElementById('fileInput')?.click()} // Tıklayınca input'u tetikle
                >
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/jpeg, image/png, image/gif, image/webp"
                    multiple
                    onChange={handleFileChange}
                    className="hidden" // Input'u gizle
                    disabled={selectedFiles.length >= 20}
                  />
                  <div className="flex flex-col items-center">
                    <UploadOutlined className={`text-4xl mb-3 ${isDragging ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${isDragging ? 'text-indigo-700' : 'text-gray-600'}`}>
                      Görselleri buraya sürükleyin veya seçmek için tıklayın
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      JPEG, PNG, GIF, WebP (Maks 5MB her biri)
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Yüklenen: {selectedFiles.length} / 20
                    </p>
                  </div>
                  {/* Limit dolduğunda uyarı */}
                  {selectedFiles.length >= 20 && (
                    <div className="absolute inset-0 bg-gray-200 bg-opacity-75 flex items-center justify-center rounded-lg">
                      <p className="text-sm font-semibold text-gray-700">Maksimum görsel limitine ulaşıldı.</p>
                    </div>
                  )}
                </div>

                {/* Görsel Önizlemeleri - Düzeltildi */}
                {selectedFiles.length > 0 && (
                  <div className="mt-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Yüklenen Görseller:</h5>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                      {imagePreviewUrls.map((url, index) => (
                        <div key={index} className="relative group aspect-w-1 aspect-h-1">
                          <img
                            src={url}
                            alt={`Önizleme ${index + 1}`}
                            className="object-cover w-full h-full rounded-md border border-gray-200 shadow-sm"
                            onLoad={() => console.log(`[CreateProduct] Önizleme yüklendi: ${url}`)} // Optional: Log when preview loads
                            onError={() => console.error(`[CreateProduct] Önizleme yüklenemedi: ${url}`)} // Optional: Log error
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                            title="Bu görseli kaldır"
                          >
                            <DeleteOutlined style={{ fontSize: '12px' }} />
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* Hatalı ':' kaldırıldı ve buton onClick güncellendi */}
                    <div className="mt-4 flex justify-end">
                      <AntButton
                        size="small"
                        onClick={() => {
                          console.log('[CreateProduct] Tüm görseller temizleniyor...');
                          imagePreviewUrls.forEach(url => URL.revokeObjectURL(url)); // Belleği serbest bırak
                          setSelectedFiles([]);
                          setImagePreviewUrls([]);
                        }}
                      >
                        Tüm Görselleri Temizle
                      </AntButton>
                    </div>
                  </div> // Bu div doğru yerde
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Ürün hakkında detaylı açıklama..."
                />
              </div>
            </div>
          </Card>

              {/* Ana Görseller Upload */}
              <Card>
                <SectionTitle>Ana Ürün Görselleri</SectionTitle>
                <div className="pt-4">
                  {/* ... existing upload area ... */}
                </div>
              </Card>
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Detaylar ve Teknik Föy" key="2">
            <div className="space-y-8 mt-4">
          {/* Aksesuarlar Bölümü */}
          <Card>
            <SectionTitle>Aksesuarlar</SectionTitle>
            <div className="space-y-4">
              <AccessorySelector
                selectedAccessories={accessories}
                    onAccessorySelect={handleAccessorySelect}
                    onAccessoryRemove={handleAccessoryRemove}
              />
            </div>
          </Card>

          {/* Teknik Föy Bölümü */}
          <TechnicalSpecSheet 
            techSpec={techSpec}
            setTechSpec={setTechSpec}
          />

       
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Ürün Dosyaları" key="3">
            <div className="space-y-8 mt-4">
              <Card>
                <SectionTitle>Model Dosyaları</SectionTitle>
                <div className="pt-4">
                  <Alert
                    message="Model dosyaları bölümü"
                    description="Bu bölümde, ürüne ilişkin teknik detay içeren dosyaları yükleyebilirsiniz. PDF, Excel, Word, Zip ve benzeri formatlarda dosyalar ekleyebilirsiniz."
                    type="info"
                    showIcon
                    className="mb-6"
                  />
                  
                  {/* Model Dosyası Yükleme Alanı */}
               <div
                    onDragEnter={handleModelDocDragEnter}
                    onDragLeave={handleModelDocDragLeave}
                    onDragOver={handleModelDocDragOver}
                    onDrop={handleModelDocDrop}
                 className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ease-in-out mb-6
                      ${isModelDocDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'}
                      ${modelDocumentFiles.length >= 20 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    onClick={() => modelDocumentFiles.length < 20 && document.getElementById('modelDocFileInput')?.click()}
               >
                 <input
                      id="modelDocFileInput"
                   type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.txt"
                   multiple
                      onChange={handleModelDocFileChange}
                   className="hidden"
                      disabled={modelDocumentFiles.length >= 20}
                 />
                 <div className="flex flex-col items-center">
                      <UploadOutlined className={`text-4xl mb-3 ${isModelDocDragging ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <p className={`text-sm font-medium ${isModelDocDragging ? 'text-indigo-700' : 'text-gray-600'}`}>
                        Model dosyalarını buraya sürükleyin veya seçin
                   </p>
                   <p className="text-xs text-gray-500 mt-1">
                        PDF, DOC, XLSX, PPT, ZIP, RAR, TXT (Maks 10MB her biri)
                   </p>
                   <p className="text-xs text-gray-500 mt-1">
                        Yüklenen: {modelDocumentFiles.length} / 20
                   </p>
                 </div>
                    {modelDocumentFiles.length >= 20 && (
                   <div className="absolute inset-0 bg-gray-200 bg-opacity-75 flex items-center justify-center rounded-lg">
                        <p className="text-sm font-semibold text-gray-700">Maksimum dosya limitine ulaşıldı.</p>
                   </div>
                 )}
               </div>

                  {/* Model Dosyası Önizlemeleri */}
                  {modelDocumentFiles.length > 0 && (
                <div className="mt-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Yüklenen Dosyalar:</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {modelDocumentFiles.map((file, index) => (
                          <div key={`model-doc-${index}`} className="relative group aspect-w-1 aspect-h-1 flex flex-col items-center justify-center p-3 border border-gray-200 rounded-md bg-gray-50 hover:bg-gray-100">
                            <div className="w-12 h-12 mb-2">
                              {modelDocumentPreviewUrls[index] === '/pdf-icon.png' && <FilePdfOutlined style={{ fontSize: '32px' }} className="text-red-500" />}
                              {modelDocumentPreviewUrls[index] === '/excel-icon.png' && <FileExcelOutlined style={{ fontSize: '32px' }} className="text-green-500" />}
                              {modelDocumentPreviewUrls[index] === '/word-icon.png' && <FileOutlined style={{ fontSize: '32px' }} className="text-blue-500" />}
                              {modelDocumentPreviewUrls[index] === '/zip-icon.png' && <FileZipOutlined style={{ fontSize: '32px' }} className="text-orange-500" />}
                              {(modelDocumentPreviewUrls[index] === '/file-icon.png' || !modelDocumentPreviewUrls[index]) && <FileOutlined style={{ fontSize: '32px' }} className="text-gray-500" />}
                            </div>
                            <p className="text-xs text-center text-gray-600 line-clamp-2 mb-1" title={file.name}>
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        <button
                          type="button"
                              onClick={() => removeModelDocumentFile(index)}
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                              title="Dosyayı kaldır"
                        >
                          <DeleteOutlined style={{ fontSize: '12px' }} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <AntButton
                      size="small"
                      onClick={() => {
                            setModelDocumentFiles([]);
                            setModelDocumentPreviewUrls([]);
                      }}
                    >
                          Tüm Dosyaları Temizle
                    </AntButton>
                  </div>
                </div>
              )}
            </div>
          </Card>

              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mt-6">
                <h4 className="text-lg font-medium text-gray-800 mb-3">Yükleme İpuçları</h4>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Dosya isimleri içerik hakkında bilgi vermelidir (örn: "Model_Ölçüleri.xlsx")</li>
                  <li>Dosya boyutunu küçültmek için gereksiz görselleri kaldırın</li>
                  <li>PDF dosyaları tercihen maksimum 5MB boyutunda olmalıdır</li>
                  <li>Arşiv dosyaları (.zip, .rar) içinde düzenli klasör yapısı kullanın</li>
                  <li>Word ve Excel belgelerinde yazım hatalarını kontrol edin</li>
                </ul>
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>

        {/* Action buttons (outside of Tabs) */}
        <div className="flex justify-end mt-8">
            <AntButton
              type="primary"
              onClick={() => setConfirmModalOpen(true)}
              loading={loading}
            size="large"
              className="px-8"
            >
              {loading ? "Kaydediliyor..." : "Ürünü Oluştur"}
            </AntButton>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleSubmit}
        title="Ürün Oluştur"
        type="info"
        confirmText="Oluştur"
        cancelText="İptal"
      >
        <p>Ürünü oluşturmak istediğinizden emin misiniz?</p>
      </Modal>

      <Modal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={() => navigate('/products')}
        title="Oluşturmayı İptal Et"
        type="warning"
        confirmText="Evet"
        cancelText="Hayır"
      >
        <p>Ürün oluşturma işlemini iptal etmek istiyor musunuz? Girdiğiniz bilgiler kaydedilmeyecek.</p>
      </Modal>
    </div>
  );
};

export default ProductCreate;